import React from "react";
import ReactDOM from "react-dom";
import { datadogRum } from "@datadog/browser-rum";
import { MuiThemeProvider, createMuiTheme, createGenerateClassName, jssPreset } from "@material-ui/core/styles";
import { create } from "jss";
import { Provider } from "react-redux";
import history from "browserHistory";
import { BrowserRouter } from "react-router-dom";
import LDProvider from "providers/LDProvider";
import PageViewTracker from "components/PageViewTracker";
// Datepicker Provider
import MuiPickersUtilsProvider from "material-ui-pickers/MuiPickersUtilsProvider";
import MomentUtils from "material-ui-pickers/utils/moment-utils";
import JssProvider from "react-jss/lib/JssProvider";
import store from "store";
import { initMixpanel } from "./lib/mixpanel";

import App from "./components/App";
import "material-icons-font/material-icons-font.css";
import "./index.css";

import CacheBuster from "./CacheBuster";

import UserConfirmationPrompt from "modals/UserConfirmationModal/UserConfirmationPrompt";
import { getUserDetailsFromCookie } from "./helpers/user-helper";

const getEnvironment = () => {
  const currentURL = window.location.hostname;
  if (currentURL.indexOf("app.loanboss.com") > -1) {
    return "production";
  } else if (currentURL.indexOf("app.uat") > -1) {
    return "uat";
  } else {
    return "development";
  }
};

const env = getEnvironment();
if (env === "production" || env === "uat") {
  console.log("Production or UAT environment detected. Starting up...");
  datadogRum.init({
    applicationId: "e976e31a-1c71-4b9d-912c-79f4aab695f2",
    clientToken: "pub7562a387fdaaf469c770f790702c68e1",
    site: "datadoghq.com",
    service: "loanboss-main-app",
    env: `${env}`,
    /*
     * Specify a version number to identify the deployed version of your application in Datadog
     * version: '1.0.0',
     */
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input",
    allowedTracingOrigins: [/https*:\/\/api.*loanboss.*/]
  });
    datadogRum.startSessionReplayRecording();

    const userDetails = getUserDetailsFromCookie();
    if (userDetails) {
        datadogRum.setUser(userDetails);
    }
} else {
  console.log("Not a public or UAT environment. Starting up...");
}

initMixpanel();

const theme = createMuiTheme({
  overrides: {
    MuiButton: { text: {} },
    MuiFormLabel: {
      root: {
        fontSize: "17px"
      }
    },
    MuiFormControlLabel: {
      label: {
        fontSize: "17px"
      }
    },
    MuiInput: {
      root: {
        fontSize: "17px"
      },
      formControl: {
        fontSize: "17px"
      }
    },
    MuiTypography: {
      root: {
        fontSize: "17px"
      },
      subheading: {
        fontSize: "17px"
      }
    },
    MuiFormControl: {
      root: {
        fontSize: "17px"
      }
    },
    MuiGrid: {},
    MuiTab: {
      root: {
        backgroundColor: "rgba(100,0,100,0)",
        text: {
          fontSize: "17px"
        }
      }
    },
    MuiIconButton: {
      label: {}
    },
    MuiIcon: {
      root: {}
    },
    MuiTable: {
      root: {
        fontSize: "17px"
      }
    },
    MuiToolbar: {
      root: {
        backgroundColor: "rgba(100,0,100,0)"
      }
    },
    MuiTablePagination: {
      caption: {
        fontSize: "15px"
      },
      select: {
        paddingTop: "3px",
        marginRight: "6px",
        marginBottom: "-5px"
      }
    },
    MuiTableCell: {
      root: {
        fontSize: "17px"
      },
      footer: {

      },
      head: {
        fontSize: "17px"
      },
      body: {
        fontSize: "17px"
      }
    }
  },
  shadows: Array(25).fill("none"),
  palette: {
    primary: {
      light: "#5c5c5c",
      main: "#009DDF",
      dark: "#0c0c0c",
      contrastText: "#fff"
    },
    secondary: {
      light: "#ffc949",
      main: "#0f9ad6",
      dark: "#c46900",
      contrastText: "#fff"
    }
  },
  typeogrpahy: {
    htmlFontSize: "17px",
    fontSize: "17px",
    body: {
      fontSize: "17px"
    }

  },
  props: {
    MuiTableRow: {
      hover: false
    }
  }
});

const generateClassName = createGenerateClassName({
  productionPrefix: "v1"
});

const Root = (props) => (
  // eslint-disable-next-line
  <CacheBuster>
    {({ loading, isLatestVersion, refreshCacheAndReload}) => {
      if (loading) {
        return null;
      }
      if (!loading && !isLatestVersion) {
        // You can decide how and when you want to force reload
        refreshCacheAndReload();
      }

      return (
        <JssProvider jss={create(jssPreset())} generateClassName={generateClassName}>
          <Provider store={store}>
            <MuiThemeProvider theme={theme}>
              <MuiPickersUtilsProvider utils={MomentUtils}>
                <BrowserRouter
                  history={history}
                  getUserConfirmation={(message, callback) => UserConfirmationPrompt(message, callback)}
                >
                  <PageViewTracker>
                    <LDProvider clientSideID={props.config.reactAppLDClientId} >
                      <App history={history} datadogRum={datadogRum}/>
                    </LDProvider>
                  </PageViewTracker>
                </BrowserRouter>
              </MuiPickersUtilsProvider>
            </MuiThemeProvider>
          </Provider>
        </JssProvider>
      );
    }}
  </CacheBuster>
);

fetch("/config.json")
  .then(response => response.json())
  .then(data => {
    // refresh of cached configuration values
    const configKeys = Object.keys(data);

    configKeys.forEach(key => {
      const configValue = localStorage.getItem(key);

      if (configValue !== data[key] && configValue !== null) {
        localStorage.setItem(key, data[key]);
      }
    });

    return Promise.resolve(data);
  })
  .then(data => {
    ReactDOM.render(<Root config={data} />, document.getElementById("root"));

    if (module.hot) {
      module.hot.accept("components/App", () => {
        ReactDOM.render(<Root config={data}/>, document.getElementById("root"));
      });
    }
  });
